/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact/hooks';
import {
  TimeComponentPanZoomLayout,
  TimeComponentZoomControls,
  TimeComponentVerticalScrollbarContainer,
  TimeComponentVerticalScrollbar,
  TimeComponentHorizontalScrollbarContainer,
  TimeComponentHorizontalScrollbar,
  TimeComponentHorizontalTimeAxis
} from '../PRIVATE_TimeComponent';
import type {
  OnTimeComponentViewportChange,
  TimeComponentZoomParameters
} from '../PRIVATE_TimeComponent/TimeComponent.types';
import type { getPanZoomCanvasLayout } from './utils/panZoomCanvasLayoutUtils';
import type { getResolvedTimeAxesLayout } from '../PRIVATE_TimeComponent/utils/timeAxisLayoutUtils';
import type {
  getResolvedHorizontalScrollbarLayout,
  getResolvedVerticalScrollbarLayout
} from '../PRIVATE_TimeComponent/utils/scrollbarLayoutUtils';
import { GanttTimeAxisDataBody } from './GanttTimeAxisDataBody';
import { GanttDataBody } from './GanttDataBody';
import { getResolvedViewportLayout, type getRowDataLayout } from './utils/dataLayoutUtils';
import type {
  GanttCurrentTask,
  Gantt as GanttProps,
  GanttRow,
  GanttSelection as GanttSelectionProps,
  GanttTask,
  GanttTaskHover,
  OnGanttScrollPositionChange
} from './Gantt.types';
import { Size } from '../utils/UNSAFE_size';

type Props<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>> = {
  visRef: Ref<HTMLDivElement>;
  overlayControlsContainerRef: Ref<HTMLDivElement>;
  panZoomLayout: ReturnType<typeof getPanZoomCanvasLayout>;
  getTimeAxesLayout: ReturnType<typeof getResolvedTimeAxesLayout>;
  getHorizontalScrollbarLayout: ReturnType<typeof getResolvedHorizontalScrollbarLayout>;
  onHorizontalScrollChange: (newViewStartTime: number, newViewEndTime: number) => void;
  getVerticalScrollbarLayout: ReturnType<typeof getResolvedVerticalScrollbarLayout>;
  onVerticalScrollChange: (newScrollPosition: number) => void;
  rowAxisSeparator?: boolean;
  zoomParameters?: TimeComponentZoomParameters;
  zooming?: 'on' | 'off';
  isViewportDefined: boolean;
  onViewportChange?: OnTimeComponentViewportChange;
  getResolvedViewportLayout: typeof getResolvedViewportLayout;
  datatip?: GanttProps<K2, D1, D2>['datatip'];
  rowDataLayout: ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;
  isRTL: boolean;
  scrollPosition: number;
  onScrollPositionChange?: OnGanttScrollPositionChange;
  width?: Size;
  height?: Size;
  currentTaskInteraction: 'hover' | 'focus' | 'none';
  ariaActive?: { dataId: K2; ariaId: string; type: 'task' };
} & GanttSelectionProps<K2> &
  GanttTaskHover<K2> &
  GanttCurrentTask<K2>;

export function GanttView<K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>({
  visRef,
  overlayControlsContainerRef,
  panZoomLayout,
  getTimeAxesLayout,
  getHorizontalScrollbarLayout,
  onHorizontalScrollChange,
  getVerticalScrollbarLayout,
  onVerticalScrollChange,
  rowAxisSeparator = false,
  zoomParameters,
  zooming = 'on',
  isViewportDefined,
  onViewportChange,
  getResolvedViewportLayout,
  datatip,
  rowDataLayout,
  isRTL,
  scrollPosition,
  onScrollPositionChange,
  selectedIds,
  selectionMode,
  onSelectionChange,
  hoveredTaskId,
  onTaskHoverChange,
  currentTask,
  onCurrentTaskChange,
  currentTaskInteraction,
  ariaActive,
  width,
  height
}: Props<K1, K2, D1, D2>) {
  return (
    <TimeComponentPanZoomLayout
      overlayControlsContainerRef={overlayControlsContainerRef}
      {...panZoomLayout}
      width={width}
      height={height}
      overlayControls={
        zooming === 'on' && zoomParameters && isViewportDefined ? (
          <TimeComponentZoomControls
            position={panZoomLayout.overlayControlsPosition}
            zoomParameters={zoomParameters}
            onViewportChange={onViewportChange}
          />
        ) : undefined
      }
      verticalControl={
        <TimeComponentVerticalScrollbarContainer
          getLayout={getVerticalScrollbarLayout}
          onRangeChange={onVerticalScrollChange}>
          {(layout) => <TimeComponentVerticalScrollbar {...layout} />}
        </TimeComponentVerticalScrollbarContainer>
      }
      horizontalControl={
        <TimeComponentHorizontalScrollbarContainer
          getLayout={getHorizontalScrollbarLayout}
          onRangeChange={onHorizontalScrollChange}>
          {(layout) => <TimeComponentHorizontalScrollbar {...layout} />}
        </TimeComponentHorizontalScrollbarContainer>
      }
      vis={
        <GanttTimeAxisDataBody
          innerRef={visRef}
          isRTL={isRTL}
          scrollPosition={scrollPosition}
          onScrollPositionChange={onScrollPositionChange}
          isViewportDefined={isViewportDefined}
          onViewportChange={onViewportChange}
          zoomParameters={zoomParameters}
          zooming={zooming}
          rowAxisSeparator={rowAxisSeparator}
          selectedIds={selectedIds}
          selectionMode={selectionMode}
          onSelectionChange={onSelectionChange}
          hoveredTaskId={hoveredTaskId}
          onTaskHoverChange={onTaskHoverChange}
          currentTask={currentTask}
          onCurrentTaskChange={onCurrentTaskChange}
          currentTaskInteraction={currentTaskInteraction}
          ariaActive={ariaActive}
          getTimeAxesLayout={getTimeAxesLayout}
          getResolvedViewportLayout={getResolvedViewportLayout}
          rowDataLayout={rowDataLayout}
          datatip={datatip}
          majorAxis={(layout) => <TimeComponentHorizontalTimeAxis {...layout} />}
          minorAxis={(layout) => <TimeComponentHorizontalTimeAxis {...layout} />}
          dataBody={(layout) => <GanttDataBody {...layout} />}
        />
      }
    />
  );
}
